import { useState, useEffect, useMemo, useCallback } from "react";
import { getCommunityStrategies, getBenchmarks } from "../services/strategyService";
import type { StrategyListItem, BenchmarkItem } from "../services/strategyService";
import { PageHeader } from "../components/PageHeader";

// ─── Types ────────────────────────────────────────────────────────

interface StrategyAllocation {
  strategy: StrategyListItem;
  weight: number; // 1–5 multiplier
}

interface ScenarioResult {
  label: string;
  expectedReturn: number; // weighted CAGR %
  expectedMaxDD: number; // weighted max drawdown %
  projectedValue: number; // portfolio $ after 1yr
}

interface BenchmarkScenario {
  name: string;
  expectedReturn: number;
  expectedMaxDD: number;
  projectedValue: number;
}

// ─── Scenario math ────────────────────────────────────────────────
// Best   = raw metrics
// Average = CAGR ÷ 2.5,  MaxDD × 2
// Poor    = CAGR ÷ 8,  MaxDD × 3

function computeScenarios(
  allocations: StrategyAllocation[],
  portfolioValue: number,
): ScenarioResult[] {
  const totalWeight = allocations.reduce((s, a) => s + a.weight, 0);
  if (totalWeight === 0) {
    return [
      { label: "Best", expectedReturn: 0, expectedMaxDD: 0, projectedValue: portfolioValue },
      { label: "Average", expectedReturn: 0, expectedMaxDD: 0, projectedValue: portfolioValue },
      { label: "Poor", expectedReturn: 0, expectedMaxDD: 0, projectedValue: portfolioValue },
    ];
  }

  const weighted = (
    getValue: (s: StrategyListItem) => number,
  ) =>
    allocations.reduce(
      (sum, a) => sum + (a.weight / totalWeight) * getValue(a.strategy),
      0,
    );

  const rawCAGR = weighted((s) => s.cagr ?? 0);
  const rawDD = weighted((s) => Math.abs(s.max_drawdown ?? 0));

  const scenarios: { label: string; cagrDiv: number; ddMul: number }[] = [
    { label: "Best", cagrDiv: 1, ddMul: 1 },
    { label: "Average", cagrDiv: 2.5, ddMul: 2 },
    { label: "Poor", cagrDiv: 8, ddMul: 3 },
  ];

  return scenarios.map(({ label, cagrDiv, ddMul }) => {
    const expectedReturn = rawCAGR / cagrDiv;
    const expectedMaxDD = -(rawDD * ddMul);
    const projectedValue = portfolioValue * (1 + expectedReturn / 100);
    return { label, expectedReturn, expectedMaxDD, projectedValue };
  });
}

// ─── Helpers ──────────────────────────────────────────────────────

function fmt(val: number | undefined, decimals = 1, suffix = "%"): string {
  if (val === undefined || val === null) return "—";
  return `${val >= 0 ? "+" : ""}${val.toFixed(decimals)}${suffix}`;
}

function fmtUSD(val: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function metricColor(val: number | undefined): string {
  if (val === undefined || val === null) return "text-surface-400";
  return val >= 0 ? "text-emerald-400" : "text-red-400";
}

// ─── Component ────────────────────────────────────────────────────

export function DiversifierPage() {
  const [strategies, setStrategies] = useState<StrategyListItem[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [portfolioInput, setPortfolioInput] = useState("100000");

  const portfolioValue = useMemo(() => {
    const parsed = Number(portfolioInput.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 100_000;
  }, [portfolioInput]);

  // Load featured community strategies
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, benchmarkData] = await Promise.all([
          getCommunityStrategies(100, 0),
          getBenchmarks(),
        ]);
        if (cancelled) return;
        const featured = (data.strategies || []).filter((s) => s.is_featured);
        setStrategies(featured);
        setBenchmarks(benchmarkData || []);
        // Default all weights from strategy default_weight
        const initial: Record<string, number> = {};
        for (const s of featured) {
          initial[s.id] = s.default_weight ?? 1;
        }
        setWeights(initial);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load strategies");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setWeight = useCallback((id: string, w: number) => {
    setWeights((prev) => ({ ...prev, [id]: w }));
  }, []);

  // Build allocations from current weights
  const allocations: StrategyAllocation[] = useMemo(
    () => strategies.map((s) => ({ strategy: s, weight: weights[s.id] ?? 1 })),
    [strategies, weights],
  );

  const totalWeight = useMemo(
    () => allocations.reduce((s, a) => s + a.weight, 0),
    [allocations],
  );

  const scenarios = useMemo(
    () => computeScenarios(allocations, portfolioValue),
    [allocations, portfolioValue],
  );

  // Compute benchmark scenarios keyed by scenario label
  const benchmarkScenarios = useMemo(() => {
    const scenarioDefs = [
      { label: "Best", cagrDiv: 1, ddMul: 1 },
      { label: "Average", cagrDiv: 2.5, ddMul: 2 },
      { label: "Poor", cagrDiv: 8, ddMul: 3 },
    ];
    const result: Record<string, BenchmarkScenario[]> = {};
    for (const { label, cagrDiv, ddMul } of scenarioDefs) {
      result[label] = benchmarks.map((b) => {
        const cagr = (b.cagr ?? 0) / cagrDiv;
        const dd = -(Math.abs(b.max_drawdown ?? 0) * ddMul);
        return {
          name: b.name,
          expectedReturn: cagr,
          expectedMaxDD: dd,
          projectedValue: portfolioValue * (1 + cagr / 100),
        };
      });
    }
    return result;
  }, [benchmarks, portfolioValue]);

  // ── Render ────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Diversifier"
        subtitle="Explore risk-adjusted allocations across featured strategies"
        backgroundImage="https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* ── Promo Video ─────────────────────────────────── */}
        <div className="mb-8 flex justify-center">
          <div className="w-full max-w-4xl">
            <video
              controls
              preload="auto"
              className="w-full rounded-xl border border-surface-700/50 shadow-lg shadow-black/30"
            >
              <source src="/TradeCraftPromo.mp4?v=2" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* ── Quote ───────────────────────────────────────── */}
        <blockquote className="mb-6 border-l-2 border-primary-500/40 pl-4 py-1">
          <p className="text-sm italic text-surface-400">
            "Diversification is the only free lunch in investing."
          </p>
          <cite className="mt-1 block text-xs text-surface-500 not-italic">
            — Harry Markowitz, Nobel Laureate
          </cite>
        </blockquote>

        <p className="mb-6 text-sm text-surface-400">
          Enter your portfolio value below and assign a weight (0–5) to each strategy based on your risk tolerance. The projections will show how your combined allocation could perform across best, average, and poor case scenarios.
        </p>

        {/* ── Portfolio Input ──────────────────────────────── */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <label className="text-sm font-medium text-surface-300">
            Portfolio Value
          </label>
          <div className="relative w-full sm:w-64">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500 text-sm">$</span>
            <input
              type="text"
              inputMode="numeric"
              value={portfolioInput}
              onChange={(e) => setPortfolioInput(e.target.value)}
              className="input-field w-full pl-7 py-2 text-right"
              placeholder="100000"
            />
          </div>
          <span className="text-xs text-surface-500 hidden sm:inline">
            Adjust to see projected scenario values
          </span>
        </div>

        {/* ── Loading / Error ─────────────────────────────── */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading featured strategies...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-4 text-red-400 text-sm mb-6">
            {error}
          </div>
        )}

        {!isLoading && !error && strategies.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <h3 className="text-lg font-medium text-surface-300 mb-2">
              No featured strategies available
            </h3>
            <p className="text-surface-500 max-w-md">
              Check back soon — featured strategies are curated by the team.
            </p>
          </div>
        )}

        {!isLoading && !error && strategies.length > 0 && (
          <>
            {/* ── Strategy Table ───────────────────────────── */}
            <div className="rounded-lg border border-surface-700/50 bg-surface-900/60 backdrop-blur-sm overflow-hidden mb-8">
              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-700/50 bg-surface-800/50">
                      <th className="text-left px-4 py-3 text-surface-400 font-semibold">Strategy</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">CAGR 10Y</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">1M</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">3M</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">6M</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">1Y</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">Max DD</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">Sharpe</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">Sortino</th>
                      <th className="text-center px-3 py-3 text-surface-400 font-semibold">Weight</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">Alloc %</th>
                      <th className="text-right px-3 py-3 text-surface-400 font-semibold">$ Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map(({ strategy, weight }) => {
                      const allocPct = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
                      return (
                        <tr
                          key={strategy.id}
                          className="border-b border-surface-800/50 hover:bg-surface-800/30 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-surface-100">{strategy.name}</div>
                            {strategy.description && (
                              <div className="text-xs text-surface-500 mt-0.5 line-clamp-1 max-w-xs">
                                {strategy.description}
                              </div>
                            )}
                          </td>
                          <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(strategy.cagr)}`}>
                            {fmt(strategy.cagr)}
                          </td>
                          <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(strategy.trailing_1m)}`}>
                            {fmt(strategy.trailing_1m)}
                          </td>
                          <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(strategy.trailing_3m)}`}>
                            {fmt(strategy.trailing_3m)}
                          </td>
                          <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(strategy.trailing_6m)}`}>
                            {fmt(strategy.trailing_6m)}
                          </td>
                          <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(strategy.trailing_1y)}`}>
                            {fmt(strategy.trailing_1y)}
                          </td>
                          <td className="text-right px-3 py-3 font-mono text-sm text-red-400">
                            {strategy.max_drawdown !== undefined
                              ? `-${Math.abs(strategy.max_drawdown).toFixed(1)}%`
                              : "—"}
                          </td>
                          <td className="text-right px-3 py-3 font-mono text-sm text-surface-200">
                            {strategy.sharpe?.toFixed(2) ?? "—"}
                          </td>
                          <td className="text-right px-3 py-3 font-mono text-sm text-surface-200">
                            {strategy.sortino?.toFixed(2) ?? "—"}
                          </td>
                          <td className="text-center px-3 py-3">
                            <WeightSelector
                              value={weight}
                              onChange={(w) => setWeight(strategy.id, w)}
                            />
                          </td>
                          <td className="text-right px-3 py-3 font-mono text-sm text-primary-400">
                            {allocPct.toFixed(1)}%
                          </td>
                          <td className="text-right px-3 py-3 font-mono text-sm text-surface-200">
                            {weight > 0 ? fmtUSD(portfolioValue * allocPct / 100) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                    {/* ── Benchmark rows ── */}
                    {benchmarks.length > 0 && (
                      <>
                        <tr>
                          <td colSpan={12} className="px-4 py-2 bg-surface-800/40">
                            <span className="text-[10px] uppercase tracking-wider text-surface-500 font-semibold">Benchmarks</span>
                          </td>
                        </tr>
                        {benchmarks.map((b) => (
                          <tr
                            key={b.slug}
                            className="border-b border-surface-800/50 bg-surface-800/20"
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-surface-300">{b.name}</div>
                            </td>
                            <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(b.cagr)}`}>
                              {fmt(b.cagr)}
                            </td>
                            <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(b.trailing_1m)}`}>
                              {fmt(b.trailing_1m)}
                            </td>
                            <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(b.trailing_3m)}`}>
                              {fmt(b.trailing_3m)}
                            </td>
                            <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(b.trailing_6m)}`}>
                              {fmt(b.trailing_6m)}
                            </td>
                            <td className={`text-right px-3 py-3 font-mono text-sm ${metricColor(b.trailing_1y)}`}>
                              {fmt(b.trailing_1y)}
                            </td>
                            <td className="text-right px-3 py-3 font-mono text-sm text-red-400">
                              {b.max_drawdown !== undefined
                                ? `-${Math.abs(b.max_drawdown).toFixed(1)}%`
                                : "—"}
                            </td>
                            <td className="text-right px-3 py-3 font-mono text-sm text-surface-200">
                              {b.sharpe?.toFixed(2) ?? "—"}
                            </td>
                            <td className="text-right px-3 py-3 font-mono text-sm text-surface-400">—</td>
                            <td className="text-center px-3 py-3 text-surface-600">—</td>
                            <td className="text-right px-3 py-3 text-surface-600">—</td>
                            <td className="text-right px-3 py-3 text-surface-600">—</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden divide-y divide-surface-800/50">
                {allocations.map(({ strategy, weight }) => {
                  const allocPct = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
                  return (
                    <div key={strategy.id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 mr-3">
                          <div className="font-medium text-surface-100 truncate">{strategy.name}</div>
                          <div className="text-xs text-primary-400 mt-0.5">
                            {allocPct.toFixed(1)}% allocation
                          </div>
                        </div>
                        <WeightSelector
                          value={weight}
                          onChange={(w) => setWeight(strategy.id, w)}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <MetricCell label="CAGR 10Y" value={fmt(strategy.cagr)} color={metricColor(strategy.cagr)} />
                        <MetricCell label="1M" value={fmt(strategy.trailing_1m)} color={metricColor(strategy.trailing_1m)} />
                        <MetricCell label="3M" value={fmt(strategy.trailing_3m)} color={metricColor(strategy.trailing_3m)} />
                        <MetricCell label="6M" value={fmt(strategy.trailing_6m)} color={metricColor(strategy.trailing_6m)} />
                        <MetricCell label="1Y" value={fmt(strategy.trailing_1y)} color={metricColor(strategy.trailing_1y)} />
                        <MetricCell
                          label="Max DD"
                          value={strategy.max_drawdown !== undefined ? `-${Math.abs(strategy.max_drawdown).toFixed(1)}%` : "—"}
                          color="text-red-400"
                        />
                        <MetricCell label="Sharpe" value={strategy.sharpe?.toFixed(2) ?? "—"} color="text-surface-200" />
                        <MetricCell label="Sortino" value={strategy.sortino?.toFixed(2) ?? "—"} color="text-surface-200" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: benchmark cards */}
              {benchmarks.length > 0 && (
                <div className="lg:hidden">
                  <div className="px-4 py-2 bg-surface-800/40">
                    <span className="text-[10px] uppercase tracking-wider text-surface-500 font-medium">Benchmarks</span>
                  </div>
                  <div className="divide-y divide-surface-800/50">
                    {benchmarks.map((b) => (
                      <div key={b.slug} className="p-4 bg-surface-800/20">
                        <div className="font-medium text-surface-300 mb-3">{b.name}</div>
                        <div className="grid grid-cols-3 gap-3">
                          <MetricCell label="CAGR 10Y" value={fmt(b.cagr)} color={metricColor(b.cagr)} />
                          <MetricCell label="1M" value={fmt(b.trailing_1m)} color={metricColor(b.trailing_1m)} />
                          <MetricCell label="3M" value={fmt(b.trailing_3m)} color={metricColor(b.trailing_3m)} />
                          <MetricCell label="6M" value={fmt(b.trailing_6m)} color={metricColor(b.trailing_6m)} />
                          <MetricCell label="1Y" value={fmt(b.trailing_1y)} color={metricColor(b.trailing_1y)} />
                          <MetricCell
                            label="Max DD"
                            value={b.max_drawdown !== undefined ? `-${Math.abs(b.max_drawdown).toFixed(1)}%` : "—"}
                            color="text-red-400"
                          />
                          <MetricCell label="Sharpe" value={b.sharpe?.toFixed(2) ?? "—"} color="text-surface-200" />
                          <MetricCell label="Sortino" value="—" color="text-surface-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Scenario Projection Cards ─────────────────── */}
            <h2 className="text-lg font-semibold text-surface-100 mb-4">
              Scenario Projections
              <span className="text-xs font-normal text-surface-500 ml-2">
                Based on {fmtUSD(portfolioValue)} portfolio, 1-year horizon
              </span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {scenarios.map((sc) => {
                const colorMap: Record<string, { border: string; bg: string; text: string; icon: string }> = {
                  Best: {
                    border: "border-emerald-500/30",
                    bg: "bg-emerald-500/5",
                    text: "text-emerald-400",
                    icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                  },
                  Average: {
                    border: "border-primary-500/30",
                    bg: "bg-primary-500/5",
                    text: "text-primary-400",
                    icon: "M4 12h16",
                  },
                  Poor: {
                    border: "border-red-500/30",
                    bg: "bg-red-500/5",
                    text: "text-red-400",
                    icon: "M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6",
                  },
                };
                const c = colorMap[sc.label] ?? colorMap.Average;
                return (
                  <div
                    key={sc.label}
                    className={`rounded-lg ${c.bg} ${c.border} border backdrop-blur-sm p-5`}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <svg
                        className={`w-5 h-5 ${c.text}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d={c.icon} />
                      </svg>
                      <span className={`text-sm font-semibold ${c.text}`}>
                        {sc.label} Case
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <div className="text-xs text-surface-500 mb-0.5">Expected Return</div>
                        <div className={`text-xl font-bold font-mono ${c.text}`}>
                          {fmt(sc.expectedReturn)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-surface-500 mb-0.5">Max Drawdown</div>
                        <div className="text-lg font-bold font-mono text-red-400">
                          {sc.expectedMaxDD.toFixed(1)}%
                        </div>
                      </div>
                      <div className="pt-2 border-t border-surface-700/30">
                        <div className="text-xs text-surface-500 mb-0.5">Projected Value</div>
                        <div className={`text-xl font-bold ${c.text}`}>
                          {fmtUSD(sc.projectedValue)}
                        </div>
                      </div>

                      {/* Benchmark comparison rows */}
                      {(benchmarkScenarios[sc.label] ?? []).length > 0 && (
                        <div className="pt-3 mt-3 border-t border-surface-700/30 space-y-2">
                          <div className="text-[10px] uppercase tracking-wider text-surface-500 font-semibold">vs Benchmarks</div>
                          {(benchmarkScenarios[sc.label] ?? []).map((bs) => (
                            <div key={bs.name} className="flex items-center justify-between text-xs">
                              <span className="text-surface-400">{bs.name}</span>
                              <div className="flex items-center gap-3 font-mono">
                                <span className={metricColor(bs.expectedReturn)}>
                                  {fmt(bs.expectedReturn)}
                                </span>
                                <span className="text-surface-500">{fmtUSD(bs.projectedValue)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Allocation Breakdown Bar ──────────────────── */}
            {totalWeight > 0 && (
              <div className="rounded-lg border border-surface-700/50 bg-surface-900/60 backdrop-blur-sm p-5">
                <h3 className="text-sm font-semibold text-surface-300 mb-3">
                  Allocation Breakdown
                </h3>
                <div className="flex rounded-full overflow-hidden h-4 bg-surface-800">
                  {allocations
                    .filter((a) => a.weight > 0)
                    .map((a, i) => {
                      const pct = (a.weight / totalWeight) * 100;
                      const colors = [
                        "bg-primary-500",
                        "bg-accent-500",
                        "bg-emerald-500",
                        "bg-violet-500",
                        "bg-amber-500",
                        "bg-rose-500",
                        "bg-sky-500",
                        "bg-fuchsia-500",
                        "bg-lime-500",
                        "bg-orange-500",
                      ];
                      return (
                        <div
                          key={a.strategy.id}
                          className={`${colors[i % colors.length]} transition-all duration-300`}
                          style={{ width: `${pct}%` }}
                          title={`${a.strategy.name}: ${pct.toFixed(1)}%`}
                        />
                      );
                    })}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                  {allocations
                    .filter((a) => a.weight > 0)
                    .map((a, i) => {
                      const pct = (a.weight / totalWeight) * 100;
                      const dotColors = [
                        "bg-primary-500",
                        "bg-accent-500",
                        "bg-emerald-500",
                        "bg-violet-500",
                        "bg-amber-500",
                        "bg-rose-500",
                        "bg-sky-500",
                        "bg-fuchsia-500",
                        "bg-lime-500",
                        "bg-orange-500",
                      ];
                      return (
                        <div key={a.strategy.id} className="flex items-center gap-1.5 text-xs">
                          <span className={`w-2.5 h-2.5 rounded-full ${dotColors[i % dotColors.length]}`} />
                          <span className="text-surface-400">{a.strategy.name}</span>
                          <span className="text-surface-200 font-medium">{pct.toFixed(1)}%</span>
                          <span className="text-surface-500">({fmtUSD(portfolioValue * pct / 100)})</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* ── Disclaimer ─────────────────────────────────── */}
            <p className="mt-6 text-xs text-surface-600 leading-relaxed">
              Projections are hypothetical and based on historical backtest
              performance. Past results do not guarantee future returns. Best case
              uses raw metrics, Average divides CAGR by 2.5 and doubles max drawdown,
              Poor divides CAGR by 8 and triples max drawdown. Always do your own
              due diligence.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function WeightSelector({
  value,
  onChange,
}: {
  value: number;
  onChange: (w: number) => void;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 bg-surface-800 rounded-lg p-0.5">
      {[0, 1, 2, 3, 4, 5].map((w) => (
        <button
          key={w}
          type="button"
          onClick={() => onChange(w)}
          className={`w-7 h-7 rounded-md text-xs font-semibold transition-all duration-150 ${
            w === value
              ? w === 0
                ? "bg-surface-600 text-surface-300 shadow-sm"
                : "bg-primary-500 text-white shadow-sm"
              : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
          }`}
        >
          {w}
        </button>
      ))}
    </div>
  );
}

function MetricCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="bg-surface-800/40 rounded-md p-2">
      <div className="text-[10px] text-surface-500 mb-0.5">{label}</div>
      <div className={`text-sm font-mono font-medium ${color}`}>{value}</div>
    </div>
  );
}
